import { gql } from '@apollo/client';
import { Avatar } from '@apollo/orbit';
import { List } from '@apollo/space-kit/List';
import { ListItem } from '@apollo/space-kit/ListItem';
import { Select } from '@apollo/space-kit/Select';
import classnames from 'classnames';
import { keyBy, orderBy } from 'lodash';
import React, { useMemo } from 'react';

import { GraphSelectFragment } from 'src/app/graph/sandboxShared/GraphSelect';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export const MultiOrgSelectFragment = gql`
  fragment MultiOrgSelectFragment on Account {
    id
    name
    avatarUrl
    services {
      id
      ...GraphSelectFragment
    }
    roles {
      canCreateService
    }
  }
  ${GraphSelectFragment}
`;

export const MultiOrgSelect = ({
  triggerContent,
  selectedOrgId,
  setSelectedOrgId,
  orgs,
  disabledItemChecker = () => undefined,
  additionalListItemsByKey,
  onBlur,
}: {
  triggerContent: React.ReactNode;
  selectedOrgId: string | null;
  setSelectedOrgId: (orgId: string) => void;
  orgs: GraphQLTypes.MultiOrgSelectFragment[];
  disabledItemChecker?: (
    org: GraphQLTypes.MultiOrgSelectFragment,
  ) => { isDisabled: boolean; disabledExplanation: string } | undefined;
  additionalListItemsByKey?: Record<
    string,
    (props: React.ComponentProps<typeof ListItem>) => React.ReactElement
  >;
  onBlur?: React.FocusEventHandler<HTMLSelectElement>;
}) => {
  const keyedOrgs = useMemo(() => keyBy(orgs, (org) => org.id), [orgs]);

  return (
    <Select
      feel="flat"
      size="standard"
      data-testid="org_select"
      listAs={<List className="max-h-72 w-72 outline-none" />}
      renderTriggerNode={() => <>{triggerContent}</>}
      renderListItem={(itemProps, options) => {
        const optionValue =
          typeof options.props.value === 'string'
            ? options.props.value
            : undefined;

        const additionalItem =
          optionValue && additionalListItemsByKey?.[optionValue];
        if (additionalItem) {
          return additionalItem(itemProps);
        }

        const org = optionValue && keyedOrgs[optionValue];
        if (!org) {
          throw Error(
            `Could not find orgId: ${optionValue} in the passed orgs`,
          );
        }

        const { isDisabled, disabledExplanation } =
          disabledItemChecker(org) ?? {};

        return (
          <ListItem
            interactive={!isDisabled}
            {...itemProps}
            as={<div />}
            className={classnames(isDisabled && 'opacity-30')}
          >
            <Tooltip
              label={disabledExplanation}
              disabled={!isDisabled || !disabledExplanation}
            >
              <div className="flex items-center gap-2">
                <Avatar
                  name={org.name}
                  src={org.avatarUrl || undefined}
                  size="xs"
                  variant="org"
                />
                <span
                  className={classnames(
                    'truncate text-base',
                    itemProps.selected && 'font-semibold',
                  )}
                >
                  {org.name}
                </span>
              </div>
            </Tooltip>
          </ListItem>
        );
      }}
      className="max-w-44 truncate border-none"
      value={selectedOrgId}
      onChange={(e) => {
        setSelectedOrgId(e.target.value);
      }}
      onBlur={onBlur}
      truncate={false}
    >
      {orderBy(
        Object.values(orgs),
        [
          (org) => (disabledItemChecker(org)?.isDisabled ? 1 : -1),
          (org) => org.name.toLowerCase(),
        ],
        ['asc', 'asc'],
      ).map((org) => {
        const isDisabled = disabledItemChecker(org)?.isDisabled;
        return <option value={org.id} key={org.id} disabled={isDisabled} />;
      })}
      {additionalListItemsByKey &&
        Object.keys(additionalListItemsByKey).map((key) => (
          <option value={key} key={key} />
        ))}
    </Select>
  );
};
